import { Button } from "@/components/ui/baseUi/button"
import { UserCell } from "@/components/ui/layout/table/userCell"
import { RadioGroup, RadioGroupItem } from "@/components/ui/baseUi/radio-group"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { NoticeIcon } from "@images/svgIcons/noticeIcon"
import { BoxB2BSubscribedProgramsEntity } from "@modules/boxB2BProgram/entities/boxB2BSubscribedProgramsEntity"
import { useBoxExploreSidebar } from "@modules/boxB2BProgram/hooks/useBoxExploreB2BProgramsSidebar"
import { useBoxSubscribedB2BPrograms } from "@modules/boxB2BProgram/hooks/useBoxSubscribedB2BPrograms"
import { ConfigService } from "@modules/core/services/configService"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { createColumnHelper } from "@tanstack/react-table"
import { useCallback, useEffect, useRef } from "react"

type BoxProgrammationImportProgramSelectionProps = {
  selectProgramId?: string | null
  setSelectProgramId: (programId: string | null) => void
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<BoxB2BSubscribedProgramsEntity>()

export function BoxProgrammationImportProgramSelection({
  selectProgramId,
  setSelectProgramId,
  className,
  ...props
}: BoxProgrammationImportProgramSelectionProps) {
  const defaultClassName = "flex flex-col bg-neutral-100 p-6 gap-4 rounded-4xl border-borderPrimary border-2"
  const crossfitAffiliateProgramId = ConfigService.config.CROSSFIT_AFFILIATE_PROGRAM_ID
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const {
    data: { content: boxSubscribedB2BPrograms },
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBoxSubscribedB2BPrograms()
  const { navigateToBoxExploreB2BPrograms } = useBoxExploreSidebar()
  const { navigateToProgramView } = useProgramSidebar()
  const mySpaceNavigate = useMySpaceNavigate()
  const confirmPopup = useConfirmPopup()

  const openProgramView = (program: BoxB2BSubscribedProgramsEntity) => {
    if (program.id === crossfitAffiliateProgramId) return

    if (program.subscribedByUser) {
      confirmPopup.show({
        title: word("box.programmation.activity.importTemplateForm.subscribedProgram.spaceRedirection.title"),
        message: word("box.programmation.activity.importTemplateForm.subscribedProgram.spaceRedirection.content"),
        accept: () => {
          mySpaceNavigate(Path.MySpace.ProgramDetails(program.id))
        },
      })
    } else navigateToProgramView(program.id, false, { replaceAllComponents: false })
  }

  const columns = [
    columnHelper.display({
      id: "selector",
      header: "",
      cell: ({ row: { original: program } }) => <RadioGroupItem value={program.id} id={program.id} />,
      meta: {
        className: "p-0 px-2 cursor-pointer",
      },
    }),
    columnHelper.accessor("name", {
      header: () => (
        <Text fontStyle="LM">{word("box.programmation.activity.importTemplateForm.subscribedProgram.name")}</Text>
      ),
      cell: ({ row: { original: program } }) => (
        <Text fontStyle="BS" className="w-full">
          {program.name}
        </Text>
      ),
      meta: {
        headerClassName: "px-2",
        className: "overflow-hidden px-2 py-2 text-ellipsis cursor-pointer",
        style: { whiteSpace: "nowrap", maxWidth: 220 },
      },
    }),
    columnHelper.accessor("owner", {
      header: () => (
        <Text fontStyle="LM">{word("box.programmation.activity.importTemplateForm.subscribedProgram.founder")}</Text>
      ),
      cell: ({ row: { original: program } }) => (
        <UserCell
          avatarSize="size-8"
          member={{
            avatar: program.owner?.avatar ?? undefined,
            username: program.owner?.firstname,
            firstname: program.owner?.firstname ?? "-",
            lastname: program.owner?.lastname ?? "-",
          }}
          subtitle={<Text fontStyle="BS">{program.owner?.username}</Text>}
        />
      ),
      meta: {
        headerClassName: "px-2",
        className: "overflow-hidden px-2 py-4 text-ellipsis cursor-pointer",
        style: { whiteSpace: "nowrap", maxWidth: 170 },
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: ({ row: { original: program } }) => {
        const isCrossfitAffiliateProgram = program.id === crossfitAffiliateProgramId
        if (isCrossfitAffiliateProgram) return null

        return (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="iconMd"
                  onClick={(e) => {
                    e.stopPropagation()
                    openProgramView(program)
                  }}
                >
                  <NoticeIcon color="currentColor" />
                </Button>
              }
            />
            <TooltipContent side="bottom">
              <Text fontStyle="LM" className="text-textDefault-body">
                {word("box.programmation.activity.importTemplateForm.subscribedProgram.seeProgram")}
              </Text>
            </TooltipContent>
          </Tooltip>
        )
      },
      meta: {
        className: "overflow-hidden px-2 text-ellipsis cursor-pointer",
      },
    }),
  ]

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={className ?? defaultClassName} {...props}>
      <Text fontStyle="BL">{word("box.programmation.activity.importTemplateForm.subscribedProgram.title")}</Text>

      <div className="flex flex-col gap-2">
        <RadioGroup value={selectProgramId ?? undefined}>
          <HUDatatable
            data={boxSubscribedB2BPrograms}
            columns={columns}
            handleRowSelectionChange={(program) => setSelectProgramId(program.id)}
            hideFooter
          />
        </RadioGroup>
        {(hasNextPage || isFetchingNextPage) && (
          <div ref={loadMoreRef} className="flex w-full items-center justify-center">
            <Text fontStyle="BM">{word("global.loading")}</Text>
          </div>
        )}
      </div>

      {boxSubscribedB2BPrograms.length === 0 && (
        <Text fontStyle="BS" className="mt-6 flex flex-1 justify-center">
          {word("box.programmation.activity.importTemplateForm.subscribedProgram.empty")}
        </Text>
      )}

      <Button
        variant="primary"
        className="w-full"
        onClick={() => navigateToBoxExploreB2BPrograms({ replaceAllComponents: false })}
      >
        {word("box.programmation.activity.importTemplateForm.explorePrograms")}
      </Button>
    </div>
  )
}
